import React, { useRef, useState } from 'react';

import { padding } from '@lumapps/classnames';
import { IconButtonProps, Placement, Popover } from '@lumapps/lumx/react';
import { SegmentInformation } from '@lumapps/segment/components/SegmentInformation/SegmentInformation';
import { SegmentInformationSkeleton } from '@lumapps/segment/components/SegmentInformation/SegmentInformationSkeleton';
import { SEGMENT_IN_GROUP_PICKER_IDENTIFIER } from '@lumapps/segment/constants';
import { useSegment } from '@lumapps/segment/hooks/useSegment';

import { Group } from '../types';

export interface UseGetSegmentActionOptions {
    group: Group;
    isOpen?: boolean;
    setIsOpen?: (isOpen: boolean) => void;
}

/**
 * Provide props and component to display the segment information in the GroupBlock component
 * @param group
 * @returns actions props & popover component
 */
export const useGetSegmentAction = ({ group, ...props }: UseGetSegmentActionOptions) => {
    const informationButtonRef = useRef<HTMLButtonElement | null>(null);
    const [isOpen, setIsOpen] = useState(props.isOpen || false);
    const { functionalInnerId } = group;
    const isSegment = functionalInnerId?.startsWith(SEGMENT_IN_GROUP_PICKER_IDENTIFIER);
    const segmentId = isSegment ? functionalInnerId?.slice(SEGMENT_IN_GROUP_PICKER_IDENTIFIER.length) : '';

    const { isLoading, segment, cancelFetch } = useSegment({
        fetchOnMount: isOpen && !!segmentId,
        segmentId,
        withCache: true,
    });

    const onMouseEnter = (evt: MouseEvent) => {
        informationButtonRef.current = evt.target as HTMLButtonElement;
        setIsOpen(true);
        props.setIsOpen?.(true);
    };

    const onMouseLeave = () => {
        cancelFetch();
        setIsOpen(false);
        props.setIsOpen?.(false);
    };

    const moreInformationButtonProps: Partial<IconButtonProps> = {
        ref: informationButtonRef,
        onMouseEnter,
        onMouseLeave,
    };

    React.useEffect(() => {
        setIsOpen(props.isOpen || false);
    }, [props.isOpen]);

    const segmentInformationAction = isSegment
        ? { onMoreInformation: () => setIsOpen(true), moreInformationButtonProps, moreInformationLabel: '' }
        : {};

    const SegmentInformationContent =
        isLoading || !segment ? <SegmentInformationSkeleton /> : <SegmentInformation segment={segment} />;

    const SegmentInformationPopover = isSegment
        ? () => {
              return (
                  <Popover
                      isOpen={isOpen}
                      closeOnEscape
                      closeOnClickAway
                      className={padding('all', 'big')}
                      anchorRef={informationButtonRef}
                      placement={Placement.BOTTOM_START}
                      style={{ maxWidth: '300px' }}
                  >
                      {SegmentInformationContent}
                  </Popover>
              );
          }
        : null;

    return { segmentInformationAction, SegmentInformationPopover, SegmentInformationContent };
};
